import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { demandAPI } from '../../services/api';
import { ProductListSkeleton } from '../../components/skeletons';

export default function MyDemands() {
  const navigate = useNavigate();
  const [demands, setDemands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    demandAPI.mine()
      .then(r => setDemands(r.data.data || r.data || []))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  const getResolvedStatus = d => {
    if (d.status === 'closed') return 'closed';
    if (d.status === 'expired') return 'expired';
    if (d.status === 'active' && d.expiresAt) {
      const isPast = new Date(d.expiresAt) - new Date() <= 0;
      if (isPast) return 'expired';
    }
    return d.status || 'active';
  };

  const filtered = filter === 'all' ? demands : demands.filter(d => getResolvedStatus(d) === filter);

  const statusColor = s => {
    if (s === 'active') return 'tag-green';
    if (s === 'closed') return 'tag-blue';
    if (s === 'expired') return 'tag-red';
    return 'tag-gold';
  };

  const timeLeft = d => {
    const resolvedStatus = getResolvedStatus(d);
    if (resolvedStatus !== 'active' || !d.expiresAt) return null;
    const diff = new Date(d.expiresAt) - new Date();
    if (diff <= 0) return 'Expired';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <ProductListSkeleton count={6} columns={3} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div className="heading-md">My Demands</div>
          <p className="text-muted text-sm" style={{ marginTop: 4 }}>{demands.length} total demands</p>
        </div>
        <button className="btn btn-pri btn-md" onClick={() => navigate('/buyer/post-demand')}>
          + Post New Demand
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', 'active', 'closed', 'expired'].map(f => (
          <button
            key={f}
            className={`btn btn-sm ${filter === f ? 'btn-pri' : 'btn-out'}`}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'all' ? ` (${demands.length})` : ` (${demands.filter(d => getResolvedStatus(d) === f).length})`}
          </button>
        ))}
      </div>

      {/* Demand Cards */}
      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <div className="heading-sm" style={{ marginBottom: 6 }}>
            {filter === 'all' ? 'No demands posted yet' : `No ${filter} demands`}
          </div>
          <p className="text-muted text-sm">
            {filter === 'all' ? 'Post your first demand to get started.' : 'Try changing the filter above.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(d => (
            <div
              key={d._id}
              className="card"
              style={{ cursor: 'pointer', padding: '18px 22px' }}
              onClick={() => navigate(`/buyer/demands/${d._id}`)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--char)' }}>{d.title}</span>
                    <span className={`tag ${statusColor(getResolvedStatus(d))}`}>{getResolvedStatus(d).toUpperCase()}</span>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {d.categoryId?.name && <span className="tag tag-green">{d.categoryId.name}</span>}
                    {d.subCategoryId?.name && <span className="tag tag-gold">{d.subCategoryId.name}</span>}
                    {d.brandId?.name && <span className="tag" style={{ background: 'var(--gray-lt)', color: 'var(--muted)' }}>{d.brandId.name}</span>}
                    {d.nationwide && <span className="tag tag-blue">NATIONWIDE</span>}
                    {d.wantsLoan && <span className="tag" style={{ background: '#FFF4D6', color: '#8A5A00' }}>🏦 FINANCING</span>}
                  </div>

                  <div className="text-muted text-xs" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {!d.nationwide && d.location?.city && <span>📍 {d.location.city}{d.location.state ? `, ${d.location.state}` : ''}</span>}
                    <span>📦 Qty: {d.qty || 1}</span>
                    {d.wantsLoan && d.downPayment != null && <span>💰 Down ₹{Number(d.downPayment).toLocaleString('en-IN')}</span>}
                    {timeLeft(d) && <span>⏱ {timeLeft(d)}</span>}
                    <span>📅 {new Date(d.createdAt).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: 'var(--font-serif)', fontWeight: 700, fontSize: 18, color: 'var(--green)' }}>
                    {d.price != null ? `\u20B9${Number(d.price).toLocaleString('en-IN')}` : '--'}
                  </div>
                  <div style={{
                    fontSize: 12, fontWeight: 700, marginTop: 4,
                    color: (d.offersCount || d.offers?.length || 0) > 0 ? 'var(--gold-dk)' : 'var(--muted)',
                  }}>
                    {d.offersCount || d.offers?.length || 0} offers
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
